import { useState } from 'react';
import { Box, Container, Typography, Stack, Button, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLanguage } from '../contexts/LanguageContext';
import { blogPosts } from '../data/blogMockData';
import BlogPostCard from '../components/BlogPostCard';
import SEOHead from '../components/SEOHead';
import { BlogCategory } from '../types/blog';
import { formatCategory } from '../utils/formatters';

const HeroSection = styled(Box)(({ theme }) => ({
  padding: '160px 0 80px',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)'
    : 'linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '800px',
    height: '800px',
    background: theme.palette.mode === 'dark'
      ? 'radial-gradient(circle, rgba(37, 99, 235, 0.1) 0%, transparent 70%)'
      : 'radial-gradient(circle, rgba(37, 99, 235, 0.05) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: '80px 0',
  background: theme.palette.mode === 'dark'
    ? '#0F172A'
    : '#FFFFFF',
}));

const FilterButton = styled(Button)<{ active?: boolean }>(({ theme, active }) => ({
  padding: '10px 24px',
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.95rem',
  border: `2px solid ${active 
    ? theme.palette.primary.main 
    : theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  background: active
    ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
    : theme.palette.mode === 'dark'
    ? 'rgba(30, 41, 59, 0.5)'
    : 'rgba(255, 255, 255, 0.9)',
  color: active ? '#FFFFFF' : theme.palette.text.primary,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 24px rgba(16, 185, 129, 0.2)'
      : '0 8px 24px rgba(37, 99, 235, 0.15)',
  },
}));

export default function Blog() {
  const { language, t } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState<BlogCategory | 'all'>('all');

  const filteredPosts = selectedCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const categories = [
    { value: 'all' as const, label: language === 'tr' ? 'Tüm Kategoriler' : 'All Categories' },
    ...Object.values(BlogCategory).map(cat => ({
      value: cat,
      label: formatCategory(cat, language),
    })),
  ];

  return (
    <>
      <SEOHead 
        title={language === 'tr' ? 'Blog - Teknolobi' : 'Blog - Teknolobi'}
        description={language === 'tr' 
          ? 'Teknoloji dünyasından en güncel haberler ve içgörüler'
          : 'Latest news and insights from the technology world'}
      />
      
      <Box component="main" role="main">
        <HeroSection>
          <Container maxWidth="lg">
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-16px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '120px',
                    height: '5px',
                    borderRadius: '3px',
                    background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  },
                }}
              >
                {language === 'tr' ? 'Blogumuz' : 'Our Blog'}
              </Typography>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ maxWidth: 700, lineHeight: 1.8, fontSize: '1.2rem', mt: 4 }}
              >
                {language === 'tr' 
                  ? 'Teknoloji dünyasından en güncel haberler ve içgörüler'
                  : 'Latest news and insights from the technology world'}
              </Typography>
            </Stack>
          </Container>
        </HeroSection>

        <ContentSection>
          <Container maxWidth="lg">
            <Stack spacing={6}>
              {/* Category Filter */}
              <Stack
                direction="row"
                spacing={2}
                flexWrap="wrap"
                useFlexGap
                justifyContent="center"
              >
                {categories.map((category) => (
                  <FilterButton
                    key={category.value}
                    active={selectedCategory === category.value}
                    onClick={() => setSelectedCategory(category.value)}
                  >
                    {category.label}
                  </FilterButton>
                ))}
              </Stack>

              {/* Blog Posts Grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, 1fr)',
                    lg: 'repeat(3, 1fr)',
                  },
                  gap: 4,
                }}
              >
                {filteredPosts.map((post) => (
                  <BlogPostCard key={post.id} post={post} />
                ))}
              </Box>

              {filteredPosts.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    {language === 'tr' 
                      ? 'Bu kategoride henüz yazı bulunmuyor.'
                      : 'No posts found in this category yet.'}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Container>
        </ContentSection>
      </Box>
    </>
  );
}